import { type CSSProperties, useMemo } from 'react';

import classnames from 'classnames';

import { computeAlignment } from '../utils/computeAlignment';
import { computeFont } from '../utils/fonts';
import { getCheckboxRadius } from '../utils/getCheckboxRadius';

import { AdditionalFields } from '../shared/additionalFields';
import { buttonStyle } from '../surveyElement/Survey.css';
import type { AlignType } from '../types';
import * as styles from './ContactForm.css';
import type { ContactFormElementProps } from './ContactForm.types';

export function ContactFormElement(props: ContactFormElementProps) {
  const { element, children, attributes, className, isLoading } = props;

  const {
    textAlign,
    mobile_hide,
    desktop_hide,
    customAttributes,
    gap,
    data,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    background,
    shadow,
    inputBorderRadius,
    inputBorderWidth,
    inputBorderColor,
    inputBorderStyle,
    inputLabelColor,
    labelFontFamily,
    buttonColor,
    requiredColor,
    buttonTextColor,
    buttonTextFontFamily,
    padding,
    inputBackgroundColor,
    inputTextColor,
    inputTextFontFamily,
    inputPlaceholderColor,
    inputRadioCheckboxAccentColor,
    inputRadioCheckboxBackgroundColor,
    inputFontWeight,
    inputFontSize,
    buttonBorderRadius,
    buttonBorderWidth,
    buttonBorderColor,
    buttonBorderStyle,
    buttonFontWeight,
    buttonFontSize,
    mobilePadding,
    buttonText,
  } = element.attrs || {};

  const computedCheckboxRadius = useMemo(() => {
    return getCheckboxRadius(
      typeof inputBorderRadius === 'number' ? `${inputBorderRadius}px` : inputBorderRadius || '6px',
    );
  }, [inputBorderRadius]);

  const inputStyle: CSSProperties = {
    borderColor: inputBorderColor,
    borderWidth: inputBorderWidth,
    borderRadius: inputBorderRadius,
    borderStyle: inputBorderStyle,
    color: inputTextColor,
    height: '40px',
    '--placeholder-color': inputPlaceholderColor,
    '--input-font-family': computeFont(inputTextFontFamily),
    '--input-background-color': inputBackgroundColor,
  } as CSSProperties;

  // field.id carries the contact-form-field-* prefix for pre-WEB-6948 forms
  const additionalFields = (data?.additional_fields || []).map((field) => ({
    ...field,
    name_override: field.name_override || field.id,
  }));

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        customAttributes?.className,
        !props.isLive && 'pointer-events-none',
        styles.contactForm,
      )}
      style={
        {
          '--text-align': textAlign,
          '--text-align-items': computeAlignment(textAlign as AlignType),
          borderRadius,
          borderWidth,
          borderColor,
          borderStyle,
          boxShadow: shadow,
          background,
          '--gap': gap,
          '--input-label-color': inputLabelColor,
          '--question-label-font-family': computeFont(labelFontFamily),
          '--question-required-color': requiredColor,
          '--button-color': buttonColor,
          '--button-text-color': buttonTextColor,
          '--button-text-font-family': computeFont(buttonTextFontFamily),
          '--input-background-color': inputBackgroundColor,
          '--input-text-color': inputTextColor,
          '--input-text-font-family': computeFont(inputTextFontFamily),
          '--input-font-family': computeFont(inputTextFontFamily),
          '--input-font-weight': inputFontWeight,
          '--input-font-size': inputFontSize,
          '--input-placeholder-color': inputPlaceholderColor,
          '--placeholder-color': inputPlaceholderColor,
          '--input-border-radius': inputBorderRadius,
          '--input-border-width': inputBorderWidth,
          '--input-border-color': inputBorderColor,
          '--input-border-style': inputBorderStyle,
          '--input-checkbox-radius': computedCheckboxRadius,
          '--input-radio-checkbox-accent-color': inputRadioCheckboxAccentColor,
          '--input-radio-checkbox-background-color': inputRadioCheckboxBackgroundColor,
          '--mobile-padding': mobilePadding ?? padding,
          '--padding': padding,
          '--border-radius': borderRadius,
          width: '100%',
        } as CSSProperties
      }
      {...attributes}
    >
      <div className="relative flex flex-col w-full" style={{ gap: 'var(--gap)' }}>
        <AdditionalFields data={additionalFields} style={inputStyle} isLabelVisible />
        <input
          type="submit"
          className={buttonStyle}
          disabled={isLoading}
          value={isLoading ? 'Submitting...' : buttonText || 'Submit'}
          style={{
            borderColor: buttonBorderColor,
            borderWidth: buttonBorderWidth,
            borderStyle: buttonBorderStyle,
            borderRadius: buttonBorderRadius,
            fontFamily: computeFont(buttonTextFontFamily),
            fontWeight: buttonFontWeight,
            fontSize: buttonFontSize,
          }}
        />
      </div>
      {children}
    </div>
  );
}
